import React, { useState, useMemo } from 'react';
import { X, BookOpen, FileText, ChevronRight, Crown, Trash2, Link, Image as ImageIcon } from 'lucide-react';
import { parseMarkdown } from '../utils/markdownUtils';

interface AdminAttachment {
    id: string;
    type: string;
    content: string;
    mimeType: string;
}

interface AdminNodeData {
    id: string;
    parentId: string | null;
    title: string;
    content: string;
    imageBase64: string | null;
    imageMimeType: string | null;
    attachments: AdminAttachment[];
    tags: string[];
    siblingOrder: number;
}

interface AdminNotebookData {
    id: string;
    title: string;
    createdAt: number;
    updatedAt: number;
    isDeleted: boolean;
    pdfContextUrl: string | null;
    nodes: AdminNodeData[];
}

interface AdminUserInfo {
    uid: string;
    name: string;
    email: string;
    photoURL: string | null;
    isPremium: boolean;
}

interface TreeNode {
    node: AdminNodeData;
    children: TreeNode[];
    depth: number;
}

function buildTree(nodes: AdminNodeData[]): TreeNode[] {
    const nodeIds = new Set(nodes.map(n => n.id));
    const sorted = [...nodes].sort((a, b) => a.siblingOrder - b.siblingOrder);

    const build = (parentId: string | null, depth: number): TreeNode[] =>
        sorted
            .filter(n => (parentId === null ? (!n.parentId || !nodeIds.has(n.parentId!)) : n.parentId === parentId))
            .map(n => ({ node: n, children: build(n.id, depth + 1), depth }));

    return build(null, 0);
}

function flattenTree(tree: TreeNode[]): { node: AdminNodeData; depth: number }[] {
    const result: { node: AdminNodeData; depth: number }[] = [];
    const walk = (nodes: TreeNode[]) => {
        for (const t of nodes) {
            result.push({ node: t.node, depth: t.depth });
            walk(t.children);
        }
    };
    walk(tree);
    return result;
}

interface Props {
    user: AdminUserInfo;
    notebooks: AdminNotebookData[];
    onClose: () => void;
}

export const AdminUserViewer: React.FC<Props> = ({ user, notebooks, onClose }) => {
    const [selectedNbId, setSelectedNbId] = useState<string>(
        notebooks.find(nb => !nb.isDeleted)?.id || notebooks[0]?.id || ''
    );
    const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

    const selectedNb = notebooks.find(nb => nb.id === selectedNbId);

    const flatNodes = useMemo(() => {
        if (!selectedNb) return [];
        return flattenTree(buildTree(selectedNb.nodes));
    }, [selectedNb]);

    const activeCount = notebooks.filter(nb => !nb.isDeleted).length;
    const totalNotes = notebooks.filter(nb => !nb.isDeleted).reduce((s, nb) => s + nb.nodes.length, 0);

    return (
        <div className="fixed inset-0 z-[10002] bg-gray-50 dark:bg-slate-950 flex flex-col">
            {/* Header */}
            <div className="flex items-center gap-3 px-5 py-3 bg-white dark:bg-slate-900 border-b dark:border-slate-700 shrink-0 shadow-sm">
                {user.photoURL
                    ? <img src={user.photoURL} className="w-9 h-9 rounded-full" alt="" />
                    : <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-400 to-purple-400 flex items-center justify-center text-white text-sm font-bold shrink-0">
                        {(user.name || user.email).charAt(0).toUpperCase()}
                      </div>
                }
                <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                        <p className="font-semibold text-sm text-gray-900 dark:text-white truncate">{user.name || '—'}</p>
                        {user.isPremium && <Crown size={12} className="text-amber-500 shrink-0" fill="currentColor" />}
                    </div>
                    <p className="text-xs text-gray-500 truncate">{user.email}</p>
                </div>
                <div className="ml-auto flex items-center gap-4 shrink-0">
                    <div className="text-right hidden sm:block">
                        <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">{activeCount} notebooks · {totalNotes} notes</p>
                        <p className="text-xs text-gray-400">Read-only admin view</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-500 transition-colors"
                    >
                        <X size={18} />
                    </button>
                </div>
            </div>

            {/* Body */}
            <div className="flex flex-1 min-h-0">
                {/* Left: notebook list */}
                <div className="w-56 shrink-0 border-r dark:border-slate-700 bg-white dark:bg-slate-900 overflow-y-auto py-2 flex flex-col gap-0.5 px-2">
                    {notebooks.map(nb => (
                        <button
                            key={nb.id}
                            onClick={() => setSelectedNbId(nb.id)}
                            className={`w-full text-left px-3 py-2.5 rounded-lg transition-colors ${selectedNbId === nb.id
                                ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                                : 'hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-300'
                            }`}
                        >
                            <div className="flex items-center gap-2">
                                {nb.isDeleted
                                    ? <Trash2 size={11} className="shrink-0 text-gray-400" />
                                    : <BookOpen size={11} className="shrink-0" />
                                }
                                <span className={`text-xs font-medium truncate ${nb.isDeleted ? 'line-through text-gray-400' : ''}`}>
                                    {nb.title || 'Untitled'}
                                </span>
                            </div>
                            <div className="flex items-center gap-2 mt-0.5">
                                <span className="text-[10px] text-gray-400 flex items-center gap-0.5">
                                    <FileText size={9} /> {nb.nodes.length}
                                </span>
                                {nb.pdfContextUrl && (
                                    <span className="text-[10px] text-orange-400 flex items-center gap-0.5">
                                        <Link size={9} /> PDF
                                    </span>
                                )}
                                <span className="text-[10px] text-gray-400">
                                    {nb.updatedAt ? new Date(nb.updatedAt).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' }) : '—'}
                                </span>
                            </div>
                        </button>
                    ))}
                </div>

                {/* Right: notes */}
                <div className="flex-1 overflow-y-auto p-5">
                    {!selectedNb ? (
                        <p className="text-center text-gray-400 mt-20 text-sm">Chọn notebook</p>
                    ) : flatNodes.length === 0 ? (
                        <p className="text-center text-gray-400 mt-20 text-sm">Notebook này chưa có notes</p>
                    ) : (
                        <div className="max-w-3xl mx-auto space-y-3">
                            {flatNodes.map(({ node, depth }) => {
                                const allImages = [
                                    node.imageBase64 ? { url: node.imageBase64 } : null,
                                    ...node.attachments.filter(a => a.type === 'image').map(a => ({ url: a.content })),
                                ].filter(Boolean) as { url: string }[];

                                const stripped = node.content.replace(/\*\*|__|\*|_|~~|`{1,3}|#{1,6}\s?|\$\$?/g, '').trim();
                                const isEmpty = !node.title && !stripped && allImages.length === 0;

                                return (
                                    <div
                                        key={node.id}
                                        className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-4"
                                        style={{ marginLeft: `${Math.min(depth, 4) * 20}px` }}
                                    >
                                        {depth > 0 && (
                                            <div className="flex items-center gap-1 mb-2 text-[10px] text-gray-400">
                                                <ChevronRight size={10} /> depth {depth}
                                            </div>
                                        )}
                                        <div className="flex gap-4">
                                            {/* Image thumbnails */}
                                            {allImages.length > 0 && (
                                                <div className="flex flex-col gap-2 shrink-0">
                                                    {allImages.slice(0, 4).map((img, idx) => (
                                                        <div key={idx} className="relative">
                                                            <img
                                                                src={img.url}
                                                                onClick={() => setLightboxUrl(img.url)}
                                                                className="w-24 h-24 object-contain bg-gray-50 dark:bg-slate-700 rounded-lg border border-gray-200 dark:border-slate-600 cursor-zoom-in hover:border-blue-400 transition-colors"
                                                                alt=""
                                                                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                                            />
                                                        </div>
                                                    ))}
                                                    {allImages.length > 4 && (
                                                        <div className="w-24 h-8 flex items-center justify-center text-[10px] text-gray-400">
                                                            <ImageIcon size={10} className="mr-1" /> +{allImages.length - 4}
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            <div className="flex-1 min-w-0">
                                                {node.title ? (
                                                    <h3 className="font-semibold text-sm text-gray-900 dark:text-white mb-1.5 leading-snug">{node.title}</h3>
                                                ) : null}

                                                {node.tags?.length > 0 && (
                                                    <div className="flex flex-wrap gap-1 mb-2">
                                                        {node.tags.map(tag => (
                                                            <span key={tag} className="text-[10px] bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 px-1.5 py-0.5 rounded-full">#{tag}</span>
                                                        ))}
                                                    </div>
                                                )}

                                                {stripped ? (
                                                    <div
                                                        className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed [&>p]:my-1 [&>ul]:my-1 [&>ol]:my-1 [&>ul]:pl-4 [&>ol]:pl-4 [&>h1]:font-bold [&>h2]:font-semibold [&>h3]:font-semibold [&>strong]:font-semibold [&>em]:italic [&>pre]:bg-gray-100 [&>pre]:dark:bg-slate-700 [&>pre]:rounded [&>pre]:p-1 [&>code]:bg-gray-100 [&>code]:dark:bg-slate-700 [&>code]:rounded [&>code]:px-0.5"
                                                        dangerouslySetInnerHTML={{ __html: parseMarkdown(node.content) }}
                                                    />
                                                ) : isEmpty ? (
                                                    <p className="text-xs text-gray-300 dark:text-gray-600 italic">Empty note</p>
                                                ) : null}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* Lightbox */}
            {lightboxUrl && (
                <div
                    className="fixed inset-0 z-[10003] bg-black/85 flex items-center justify-center"
                    onClick={() => setLightboxUrl(null)}
                >
                    <button
                        className="absolute top-4 right-4 text-white p-2 hover:bg-white/10 rounded-lg transition-colors"
                        onClick={() => setLightboxUrl(null)}
                    >
                        <X size={20} />
                    </button>
                    <img
                        src={lightboxUrl}
                        className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg"
                        alt=""
                        onClick={e => e.stopPropagation()}
                    />
                </div>
            )}
        </div>
    );
};
